import { USER_STORE_KEY, setLoginCookie, useUserStore } from '@/store/useUserStore';
import { useUserDetailStore } from '@/store/useUserDetailStore';
import { isServer } from '@tanstack/react-query';
import md5 from 'md5';
import { getLocale } from 'next-intl/server';
import { ComplexRequestOption, NewResponstype } from "./type";
import { AuthorizationError } from '@/errors/AuthorizationError';
import { getDeviceId, getPhoneBrand, getPhoneModel } from '@/util/deviceInfo';
import { redirectToLogin } from '@/util/loginRedirect';


const getLang = async () => {
  let lang = 'en'
  try {
    if (isServer)
      lang = await getLocale()
    else {
      lang = window.document.documentElement.lang
    }
  } catch (e) {

  }

  return lang
}


// 设备信息按浏览器运行时求值（SSR 回退 env 旧值），不再全站写死同一台"小米手机"。
// device_id 首次访问生成并持久化，存量已登录用户沿用旧共享值避免登录态失效。
const getCommonBody = () => ({
  facility: process.env.NEXT_PUBLIC_FACILITY,
  device_id: getDeviceId(),
})


/** 
 * 老接口使用
 * NOTE: 老接口的验证参数全在body
 * */
const getMixindBody = () => ({
  app_ver: process.env.NEXT_PUBLIC_VERSION,
  package: process.env.NEXT_PUBLIC_PACKAGE,
  phone_brand: getPhoneBrand(),
  phone_model: getPhoneModel(),
  deviceid: getDeviceId(),
})

// 新接口使用
// NOTE: PHONE-PLATFORM 决定后端签名 salt 的选择（web-client），必须保持 env 静态值
const getMixindHeader = () => ({
  "DEVICE-ID": getDeviceId(),
  "APP-VER": process.env.NEXT_PUBLIC_VERSION,
  "PACKAGE": process.env.NEXT_PUBLIC_PACKAGE,
  "PHONE-PLATFORM": process.env.NEXT_PUBLIC_PHONE_PLATFORM,
  "channel": process.env.NEXT_PUBLIC_CHANNEL,
  "PHONE-BRAND": getPhoneBrand(),
  "PHONE-MODEL": getPhoneModel(),
})


// 业务状态 102：登录态失效，客户端清会话并跳登录页
export const AUTH_EXPIRED_STATUS = 102

export const logoutAndGoLogin = async () => {
  if (isServer) return

  useUserStore.getState().unLogin()
  useUserDetailStore.getState().clearDetail()

  const lang = await getLang()
  redirectToLogin(lang)
}

// 混合请求 fetch 和 axios
export async function complexRequest<T>(option: ComplexRequestOption, useFetch: boolean = true): Promise<T> {

  // 注入token
  if (!isServer) {
    const userStore = window.localStorage.getItem(USER_STORE_KEY)
    if (userStore) {
      try {
        const { state: { userId, newToken, token } } = JSON.parse(userStore!)
        option.headers = Object.assign({
          uid: userId,
          token: newToken,
          authorization: token
        }, option.headers)
      } catch (error) {
        throw error
      }
    }
  }


  if (option.notSign) {
    const dataKey = option.method?.toLowerCase() === 'get' ? 'params' : 'data'
    delete option[dataKey].sign
    delete option[dataKey].timestamp
    delete option.notSign
  }


  if (useFetch) {
    // 拼接query参数
    // NOTE: 值必须 encodeURIComponent：fetch 发送时会对 URL 中的非 ASCII（如中文）
    // 自动百分号编码，若拼接时不编码，签名串（原文）与后端收到的 queryString（编码后）
    // 不一致，会被 CheckRequestID 拒绝（This request has expired）。
    // 这里与 newRequest 的 GET 签名串保持同一编码形式。
    if (option.method?.toLowerCase() === 'get' || option.method?.toLowerCase() === 'delete') {
      const query = '?' + Object.keys(option?.params || {}).
        map(key => option?.params[key] !== '' ? `${key}=${encodeURIComponent(option?.params[key])}` : key).
        join('&')
      option.url += query
    }

    let url: string = option.url!
    if (!/^http[s]/.test(url)) {
      url = new URL(option.url!, process.env.SSR_API_BASE_URL ?
        process.env.SSR_API_BASE_URL :
        process.env.NEXT_PUBLIC_API_BASE_URL).href
    }

    delete option.url

    let res = await fetch(url, {
      cache: isServer ? "no-cache" : undefined,
      ...option,
      body: JSON.stringify(option.data),
      headers: Object.assign(
        {
          'content-type': 'application/json'
        },
        option.headers),
    })

    if (!isServer && res.status === 502) {
      // 必须从子包引入：本文件处于服务端组件依赖树中，引用 @heroui/react barrel 会让
      // 每个路由的客户端入口在启动时预加载并求值全部 HeroUI 子包（日历、表格等）
      const { addToast } = await import('@heroui/toast')
      // 非组件环境无法使用 useTranslations，按当前页面语言动态加载语言包
      let message = 'Network error. Please try again.'
      try {
        const messages = (await import(`../locales/langs/${await getLang()}.json`)).default
        message = messages?.error?.BadGateway || message
      } catch (e) { }
      addToast({ description: message, color: 'danger' })
    }

    if (option.isStream) return res as any

    let data
    try {
      data = await res.json()
    } catch {
      // 网关 HTML、空 body 等非 JSON 不能把 Response 实例返回。
      // 它一旦被 React Query 预取成功，dehydrate 传给客户端组件时
      // RSC 会报 Only plain objects… Classes or null prototypes are not supported。
      console.error(`[complexRequest] non-JSON response ${res.status} ${url}`)
      data = {
        status: res.status,
        msg: 'Invalid JSON response',
        message: 'Invalid JSON response',
        data: null,
      }
    }


    if (Number(data?.status) === AUTH_EXPIRED_STATUS && !option.skipAuthRedirect) {
      await logoutAndGoLogin()
      throw new AuthorizationError()
    }


    return data as any
  } else {
    const { request } = await import('./request')
    const data = await request(option)
    if (Number((data as any)?.status) === AUTH_EXPIRED_STATUS) {
      await logoutAndGoLogin()
      throw new AuthorizationError()
    }
    return data as T
  }
}



// 创建老接口请求实例
const getOldRequest = (): typeof complexRequest => {

  return async function oldRequest<T>(option: ComplexRequestOption, useFetch: boolean = true): ReturnType<typeof complexRequest<T>> {
    option.data = Object.assign({
      ...getCommonBody(),
      ...{ timestamp: isServer ? 1 : Date.now() },
      ...getMixindBody()
    }, option.data)

    // 老接口后端从 header 读 phone-platform 记录登录/注册日志，
    // 不带会记成 UNK，与新接口(mixindHeader)对齐补上平台标识。
    option.headers = Object.assign({
      "PHONE-PLATFORM": process.env.NEXT_PUBLIC_PHONE_PLATFORM,
      "APP-Ver": process.env.NEXT_PUBLIC_VERSION,
    }, option.headers)

    // international
    // NOTE: 仅在未显式传入 lang 时才解析当前 locale。
    // 这样被 unstable_cache 包裹的请求可以显式传 lang，避免在缓存回调里读取
    // 请求级数据（getLocale 依赖 headers），否则会触发动态数据访问错误。
    if (!option.data.lang) option.data.lang = await getLang()

    option.method = option.method || 'post'

    const sign = md5(Object.keys(option.data).sort()
      .map(key => `${key}=${option.data[key]?.toString() || ''}`)
      .join('&') + process.env.NEXT_PUBLIC_OLD_API_KEY)

    option.data.sign = sign

    return complexRequest<T>(option, useFetch)
  }
}

// 取 url 自带的 query 串（不含 '?'），用于 POST 签名。
// 用 WHATWG URL 规范化后的 search，与 fetch 实际发出、后端 queryString() 收到的形式一致
// （非 ASCII / 空格等会被百分号编码）。无 query 返回空串，签名结果与只签 body 时完全相同。
const getUrlQueryString = (url?: string): string => {
  if (!url || !url.includes('?')) return ''
  try {
    return new URL(url, 'http://localhost').search.slice(1)
  } catch (e) {
    return url.slice(url.indexOf('?') + 1)
  }
}

// 新接口返回该 status 表示 newToken 失效，需要刷新后重试
export const NEW_TOKEN_EXPIRED_STATUS = -102

// 正在进行的刷新请求，保证并发时只刷新一次，其余请求等待同一个 Promise
let refreshTokenPromise: Promise<boolean> | null = null

// 从本地读取旧 token（authorization）
const getLocalToken = (): string | null => {
  if (isServer) return null
  try {
    const userStore = window.localStorage.getItem(USER_STORE_KEY)
    if (!userStore) return null
    return JSON.parse(userStore)?.state?.token ?? null
  } catch (e) {
    return null
  }
}

// 调用 retoken 接口刷新 newToken，成功后写回 store
const doRefreshToken = async (): Promise<boolean> => {
  const token = getLocalToken()
  if (!token) return false

  try {
    const res = await newRequest<NewResponstype<{ token: string }>>({
      url: '/client/common/adapter/retoken',
      method: 'post',
      headers: {
        device_id: getDeviceId(),
        authorization: token,
      } as any,
      // 刷新接口自身不再触发刷新，避免递归
      skipRefresh: true,
    }, true)

    if (res?.status === 200 && res?.data?.token) {
      useUserStore.setState({ newToken: res.data.token } as any)
      setLoginCookie('newToken', res.data.token)
      return true
    }
  } catch (e) {
  }

  return false
}

// 并发安全的刷新入口：同一时刻只发起一次刷新请求。
// 导出供流式请求使用：isStream 请求在 complexRequest 提前返回 Response、
// 不经过 newRequest 的 -102 检查，流式调用方需自行刷新后重试
export const refreshNewToken = (): Promise<boolean> => {
  if (!refreshTokenPromise) {
    refreshTokenPromise = doRefreshToken().finally(() => {
      refreshTokenPromise = null
    })
  }
  return refreshTokenPromise
}

// 创建新接口请求实例
const getNewRequest = (): typeof complexRequest => {
  return async function newRequest<T>(option: ComplexRequestOption, useFetch: boolean = true, isRetry: boolean = false): ReturnType<typeof complexRequest<T>> {
    // 在签名/注入之前保留一份原始配置，刷新 token 后用它重试
    const originalOption: ComplexRequestOption = { ...option }

    const lang = await getLang()
    const timestamp = isServer ? 1 : Date.now()

    option.headers = Object.assign({
      "REQUEST-TIMESTAMP": timestamp,
      ...getMixindHeader(),
    }, option.headers)

    // NOTE: 仅在未显式传入 Lang header 时才解析当前 locale。
    // 这样被 unstable_cache 包裹的请求可显式传 Lang，避免在缓存回调里读取
    // 请求级数据（getLocale 依赖 headers）而触发动态数据访问错误。
    if (!(option.headers as any).Lang) (option.headers as any).Lang = await getLang()

    option.method = option.method || 'get'


    // 签名：POST 或带请求体(body)的请求，按 JSON body + url 自带 query 签名，
    // 与后端 md5(rawBody . queryString . timestamp . salt) 校验完全对齐
    // （url 无 query 时即 md5(rawBody + timestamp + salt)，与 App 端 addSign1 一致）。
    // POST 参数请一律放 body；若 url 里写了 `?a=b`，这里会把它一并签入，否则后端必拒绝。
    // 无 body 的 GET/DELETE 仍按 query params 签名。
    if (option.method === 'post' || option.data != null) {
      option.data = Object.assign({
        timestamp,
        group_scene: '',
        content: '',
        ...getCommonBody(),
      }, option.data)

      const sign = md5(JSON.stringify(option.data) + getUrlQueryString(option.url) + timestamp + process.env.NEXT_PUBLIC_NEW_API_KEY)
      option.headers['REQUEST-ID'] = sign
      // option.data.sign = sign
    } else {
      option.params = Object.assign({
        timestamp,
        group_scene: '',
        content: '',
        ...getCommonBody(),
      }, option.params)


      Object.keys(option.params).forEach(key => {
        if (option.params[key] === undefined) delete option.params[key]
      })

      // 签名串与 complexRequest 拼接的 query 编码形式一致（encodeURIComponent），
      // 保证后端用实际收到的 queryString 重算 md5 时能对上（中文等非 ASCII 值）
      const reqIdOrigin = Object.keys(option.params)
        .filter(key => !Array.isArray(option.params[key]))
        .map(key => `${key}${option.params[key].toString() ? '=' + encodeURIComponent(option.params[key].toString()) : ''}`)
        .join('&') + timestamp + process.env.NEXT_PUBLIC_NEW_API_KEY

      option.headers['REQUEST-ID'] = md5(reqIdOrigin)
    }


    const res = await complexRequest<T>(option, useFetch)

    // newToken 过期：刷新后用原始配置重试一次（并发请求会共享同一次刷新）
    if (!isServer && !isRetry && !option.skipRefresh && (res as any)?.status === NEW_TOKEN_EXPIRED_STATUS) {
      const refreshed = await refreshNewToken()
      if (!refreshed) throw new AuthorizationError()
      return newRequest<T>(originalOption, useFetch, true)
    }

    return res
  }
}

export const oldRequest = getOldRequest()
export const newRequest = getNewRequest()

export const streamRequest = async ({ data, url }, { onData }, isRetry = false) => {

  const controller = new AbortController();
  const signal = controller.signal;
  let isTimeout = false

  const response = await newRequest<any>({
    url,
    method: 'post',
    data,
    signal,
    isStream: true
  }, true)

  const reader = response.body.getReader();
  const decoder = new TextDecoder('utf-8');

  const timer = setTimeout(() => {
    controller.abort()
    isTimeout = true
  }, 1000 * 150)

  let preStr = "";


  let res: any = null!
  let authStatus: number | null = null
  const readData = async () => {
    const { done, value } = await reader.read()

    if (done) {
      clearInterval(timer)
      return
    }


    const jsonStrs = decoder.decode(value)
    const strList = (preStr + jsonStrs).split('\n')
    for (const item of strList) {
      try {
        const json = JSON.parse(item)
        preStr = ''
        res = json
        const status = Number(json?.status)
        // 流式接口的业务 status 在 chunk 里，isStream 提前返回导致外层检查不到
        if (status === AUTH_EXPIRED_STATUS || status === NEW_TOKEN_EXPIRED_STATUS) {
          authStatus = status
          clearInterval(timer)
          controller.abort()
          return
        }
        onData && onData(json, controller)
      } catch (err) {
        preStr = item
      }
    }


    await readData()
  }

  try {
    await readData()

  } catch (error) {

  }

  if (authStatus === AUTH_EXPIRED_STATUS) {
    await logoutAndGoLogin()
    throw new AuthorizationError()
  }

  if (authStatus === NEW_TOKEN_EXPIRED_STATUS) {
    if (!isRetry) {
      const refreshed = await refreshNewToken()
      if (refreshed) {
        return streamRequest({ data, url }, { onData }, true)
      }
    }
    throw new AuthorizationError()
  }


  return new Promise((resolve, reject) => {
    if (isTimeout) reject(new Error('timeout!'))
    else resolve(res)
  })
}

export const sseRequest = async ({ url, data }, { onData }) => {

  const controller = new AbortController();
  const signal = controller.signal;
  let isTimeout = false

  const response = await newRequest<any>({
    url,
    method: 'post',
    data,
    signal,
    isStream: true
  }, true)

  const reader = response.body.getReader();
  const decoder = new TextDecoder('utf-8');

  const timer = setTimeout(() => {
    controller.abort()
    isTimeout = true
  }, 1000 * 30)


  let preStr = "";

  let res: any = null!
  const readData = async () => {
    const { done, value } = await reader.read()

    if (done) {
      clearInterval(timer)
      return
    }

    // const sseStr = decoder.decode(value)

    // console.log('sseStr:', sseStr)
    // const jsonData = JSON.parse(sseStr.slice(6))
    // res = jsonData
    // console.log('jsonData:', jsonData)
    // onData(jsonData, controller)

    const jsonStrs = decoder.decode(value)
    const strList = (preStr + jsonStrs).split('data: ')
    strList.map(item => {
      try {
        const json = JSON.parse(item)
        preStr = ''
        res = json
        onData && onData(json, controller)
      } catch (err) {
        preStr = item
      }
    })

    await readData()
  }

  try {
    await readData()

  } catch (error) {
    console.error(error)
  }


  return new Promise((resolve, reject) => {
    if (isTimeout) reject(new Error('timeout!'))
    else resolve(res)
  })

}